import React from 'react';
import { Link } from 'react-router-dom';
import Nav from '../components/Nav';
import Footer from '../components/Footer';
import { useScrollReveal, useCountUp } from '../hooks/useScrollReveal';
import counteropsRetail from '../assets/counterops-retail.jpg';
import counteropsTechnology from '../assets/counterops-technology.jpg';
import counteropsManagement from '../assets/counterops-management.jpg';
import counteropsFinancial from '../assets/counterops-financial.jpg';

const CounterOps = () => {
  useScrollReveal();
  useCountUp();

  React.useEffect(() => {
    // Feature showcase: crossfade photos based on scroll position
    const photos = document.querySelectorAll('.feature-photo');
    const items = document.querySelectorAll('.feature-item');
    
    const fio = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          items.forEach(i => i.classList.remove('active'));
          entry.target.classList.add('active');
          const key = entry.target.getAttribute('data-icon');
          photos.forEach(p => p.classList.toggle('active', p.getAttribute('data-key') === key));
        }
      });
    }, { threshold: 0.5, rootMargin: '-40% 0px -40% 0px' });
    
    items.forEach(i => fio.observe(i));
    if (items[0]) items[0].classList.add('active');

    return () => fio.disconnect();
  }, []);

  return (
    <>
      <Nav darkMode={true} />
      
      <section className="hero-dark">
        <div className="grid-bg"></div>
        <div className="hero-dark-inner">
          <div className="pill reveal in">COUNTEROPS</div>
          <h1 className="reveal in">Run every service counter like clockwork.</h1>
          <p className="sub reveal in">One system for acceptance, payments, and reporting at every public-facing window &mdash; postal, licensing, or agency services alike.</p>
          <div className="hero-ctas reveal in" style={{display:'flex', gap:'14px', justifyContent:'center', marginBottom:'64px'}}>
            <a href="#contact" className="btn btn-accent">Start now <span className="arrow">→</span></a>
          </div>
        </div>
        <div className="os-mock-row reveal">
          <div className="os-mock-card">
            <div className="os-mock-topbar"><span></span><span></span><span></span></div>
            <div className="os-mock-body">
              <div className="row w1"></div><div className="row w2"></div><div className="row w3"></div>
              <div className="row w2"></div><div className="row w1"></div>
            </div>
          </div>
          <div className="os-mock-card center">
            <div className="os-mock-topbar"><span></span><span></span><span></span></div>
            <div className="os-mock-body">
              <span className="os-stat-chip"><span className="dot"></span> Counter 04 &middot; ready</span>
              <div className="row w1" style={{marginTop:'8px'}}></div><div className="row w3"></div><div className="row w2"></div>
            </div>
          </div>
          <div className="os-mock-card">
            <div className="os-mock-topbar"><span></span><span></span><span></span></div>
            <div className="os-mock-body">
              <div className="row w2"></div><div className="row w1"></div><div className="row w3"></div>
            </div>
          </div>
        </div>
      </section>

      <section className="intro-line">
        <div className="wrap">
          <span className="eyebrow" style={{display:'block', marginBottom:'18px'}}>BUILT FOR EVERY COUNTER</span>
          <p className="reveal">CounterOps unifies counter service, back-office management, and last-mile handoff in one system &mdash; deployable standalone, or integrated into the ERP you already run, from SAP to Microsoft Dynamics to Oracle.</p>
        </div>
      </section>

      <section className="fog-section">
        <div className="wrap section-head reveal">
          <span className="eyebrow">The Platform</span>
          <h2>One counter. Every service.</h2>
        </div>
        <div className="feature-stack">
          <div className="feature-visual" id="featureVisual">
            <img className="feature-photo active" data-key="customer" src={counteropsRetail} alt="Counter clerk handing a customer a parcel receipt" />
            <img className="feature-photo" data-key="chip" src={counteropsTechnology} alt="Counter hardware: scale, scanner, and receipt printer" />
            <img className="feature-photo" data-key="clipboard" src={counteropsManagement} alt="Supervisor reviewing audit analytics on a tablet" />
            <img className="feature-photo" data-key="card" src={counteropsFinancial} alt="Customer tapping a card on a payment terminal" />
          </div>
          <div className="feature-items">
            <div className="feature-item" data-icon="customer">
              <span className="num">01 &middot; RETAIL &amp; CUSTOMER</span>
              <h3>Serve every transaction at the counter.</h3>
              <p>Accept domestic and international mail, parcels, and EMS. Rent and renew PO boxes, sell postal and third-party products, and stand up new franchise or agency locations in minutes.</p>
            </div>
            <div className="feature-item" data-icon="chip">
              <span className="num">02 &middot; TECHNOLOGY</span>
              <h3>Built to plug into what you already run.</h3>
              <p>Print directly to receipt hardware, interface with scales and scanners, and integrate with e-commerce portals and address databases &mdash; client or cloud, online or off.</p>
            </div>
            <div className="feature-item" data-icon="clipboard">
              <span className="num">03 &middot; MANAGEMENT</span>
              <h3>See everything, in the language your team works in.</h3>
              <p>A comprehensive audit trail, configurable reporting, and access control across every level of the organization &mdash; from a single counter to the whole network.</p>
            </div>
            <div className="feature-item" data-icon="card">
              <span className="num">04 &middot; FINANCIAL</span>
              <h3>Take payment however the customer wants to pay.</h3>
              <p>Cash, card, or cheque. Multi-currency exchange, instant electronic money orders, and inter-system transfers, aligned to international UPU CDS standards.</p>
            </div>
          </div>
        </div>
      </section>

      <section>
        <div className="wrap section-head reveal">
          <span className="eyebrow">Everything Included</span>
          <h2>Counter automation, down to the last transaction.</h2>
        </div>
        <div className="wrap">
          <div className="spec-grid cols-2 reveal-stagger">
            <div className="spec-card">
              <h3>
                <span className="icon">
                  <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="8" r="4"/>
                    <path d="M4 21c0-4 4-7 8-7s8 3 8 7"/>
                  </svg>
                </span>
                Retail / Customer
              </h3>
              <ul className="spec-list">
                <li>Agency services</li>
                <li>Domestic &amp; international letters, parcels, and EMS acceptance</li>
                <li>PO box rental and renewal, including virtual boxes</li>
                <li>Add-on services: registration, AD, insurance, COD</li>
                <li>Postal and third-party product sales</li>
                <li>Track &amp; trace via UPU IPS / IPS Light</li>
                <li>Franchise and agency configuration</li>
              </ul>
            </div>
            <div className="spec-card">
              <h3>
                <span className="icon">
                  <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="6" y="6" width="12" height="12" rx="2"/>
                    <path d="M10 2v4M14 2v4M10 18v4M14 18v4M2 10h4M2 14h4M18 10h4M18 14h4"/>
                  </svg>
                </span>
                Technology
              </h3>
              <ul className="spec-list">
                <li>Direct printing to receipt hardware</li>
                <li>Interfaces with scales, printers, scanners, and cubing devices</li>
                <li>Open integration with third-party and e-commerce APIs</li>
                <li>User-configurable for most services</li>
                <li>Client or cloud-based &mdash; key functions work offline</li>
                <li>Address-database integration for autofill</li>
                <li>Dynamic barcoding to UPU Standard S-10</li>
                <li>Supports Google Plus Codes and SmartAddress.io</li>
                <li>Windows, iOS, and Android compatible; touch-screen ready</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="fog-section stat-callout">
        <div className="wrap">
          <p className="lede reveal">One system, every window. Less hardware to manage, fewer logins, faster lines.</p>
          <div className="stats-2 reveal-stagger">
            <div className="stat"><h3 data-count="3" data-suffix="x">0</h3><p>Faster counter transactions</p></div>
            <div className="stat"><h3 data-count="100" data-suffix="%">0</h3><p>UPU standards compliant</p></div>
          </div>
        </div>
      </section>

      <section id="get-started">
        <div className="wrap">
          <div className="cta-band reveal">
            <h2>Ready to modernize your counters?</h2>
            <p>See CounterOps running against your own service catalog &mdash; no rip-and-replace required.</p>
            <div className="hero-ctas">
              <a href="/#contact" className="btn btn-accent">Request Briefing <span className="arrow">→</span></a>
              <Link to="/valor-os" className="btn btn-outline" style={{borderColor:'rgba(255,255,255,.25)', color:'#fff'}}>Explore Valor OS <span className="arrow">→</span></Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default CounterOps;
